import React, { useState, useEffect } from 'react';
import {
    Layout,
    Card,
    Button,
    Upload,
    Modal,
    Form,
    Input,
    Select,
    Tag,
    message,
    Space,
    Popconfirm,
    Alert,
    Tooltip,
    Popover,
    Menu,
    Typography,
    Calendar,
    Badge,
    List,
    Row,
    Col,
    Switch
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
    PlusOutlined,
    EyeOutlined,
    DownloadOutlined,
    DeleteOutlined,
    EditOutlined,
    InboxOutlined,
    FileTextOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    GiftOutlined,
    StarOutlined,
    SaveOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as actions from '../../store/actions/AuthActions';
import EmployeeAttendance from './EmployeeAttendance';

const { Option } = Select;
const { Dragger } = Upload;
const { Sider, Content } = Layout;
const { Title, Text } = Typography;

const EmployeeHR = ({ employeeId, hideSider = false, activeSection: controlledSection }) => {
    const [internalSection, setInternalSection] = useState('documents');
    const activeSection = controlledSection !== undefined ? controlledSection : internalSection;
    const setActiveSection = setInternalSection;

    // Documents states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [editingDocument, setEditingDocument] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);

    // Document Category state
    const [employeeCategoryId, setEmployeeCategoryId] = useState(null);

    // Events states
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [, setViewMode] = useState('all');


    // Verification states
    const [isVerified, setIsVerified] = useState(false);
    const [unverifiedReason, setUnverifiedReason] = useState('');
    const [verificationLoading, setVerificationLoading] = useState(false);

    const menuItems = [
        { key: 'documents', icon: <FileTextOutlined />, label: 'Documents' },
        { key: 'events', icon: <CalendarOutlined />, label: 'Events' },
        { key: 'attendance', icon: <ClockCircleOutlined />, label: 'Attendance' },
    ];

    // ==================== DOCUMENTS SECTION ====================

    const fetchDocuments = async () => {
        if (!employeeId) return;
        setDocumentsLoading(true);
        try {
            const response = await actions.loadEmployeeDocuments(employeeId, 1, MAX_PAGE_SIZE);
            if (response && response.results) {
                const formattedDocs = response.results.map(doc => ({
                    id: doc.id,
                    name: doc.document_name || 'Unnamed Document',
                    type: doc.document_type || 'Other',
                    fileName: doc.document_name || 'file',
                    uploadDate: doc.uploaded_at ? doc.uploaded_at.split('T')[0] : new Date().toISOString().split('T')[0],
                    document_url: doc.document_url,
                    document_id: doc.document?.id,
                    status: doc.is_active ? 'verified' : 'pending',
                    description: doc.notes || '',
                    uploadedBy: doc.uploaded_by_name || 'Unknown',
                    uploaded_by_id: doc.uploaded_by,
                    is_active: doc.is_active
                }));
                setDocuments(formattedDocs);
            } else {
                setDocuments([]);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            message.error('Failed to load documents');
            setDocuments([]);
        } finally {
            setDocumentsLoading(false);
        }
    };

    useEffect(() => {
        if (employeeId) {
            fetchDocuments();

            // Load employee data for verification status
            const loadEmployeeData = async () => {
                try {
                    const data = await actions.retrieveEmployee(employeeId);
                    if (data) {
                        setIsVerified(!!data.is_verified);
                        setUnverifiedReason(data.unverified_reason || '');
                    }
                } catch (error) {
                    console.error('Error loading employee verification data:', error);
                }
            };
            loadEmployeeData();
        }

        // Fetch EMPLOYEE category
        const fetchEmployeeCategory = async () => {
            const categoryId = await actions.getDocumentCategoryByCode('EMPLOYEE');
            setEmployeeCategoryId(categoryId);
        };
        fetchEmployeeCategory();
    }, [employeeId]);

    const handleSaveVerification = async () => {
        if (!employeeId) return;
        setVerificationLoading(true);
        try {
            const payload = {
                is_verified: isVerified,
                unverified_reason: isVerified ? '' : unverifiedReason,
            };
            const result = await actions.partialUpdateEmployee(payload, employeeId);
            if (result && !result.isAxiosError) {
                message.success('Verification status updated successfully');
            } else {
                message.error('Failed to update verification status');
            }
        } catch (error) {
            console.error('Error updating verification status:', error);
            message.error('Failed to update verification status');
        } finally {
            setVerificationLoading(false);
        }
    };

    const showModal = (document = null) => {
        setEditingDocument(document);
        if (document) {
            form.setFieldsValue({
                name: document.name,
                type: document.type,
                description: document.description
            });
        }
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingDocument(null);
        form.resetFields();
        setFileList([]);
    };

    const handleSubmit = async (values) => {
        setSubmitLoading(true);
        try {
            let documentId = editingDocument?.document_id;

            // Upload new file if selected
            if (fileList.length > 0 && fileList[0].originFileObj) {
                const uploadResult = await actions.uploadDocument(fileList[0].originFileObj, employeeCategoryId);
                if (!uploadResult || !uploadResult.id) {
                    message.error('Failed to upload file');
                    return;
                }
                documentId = uploadResult.id;

                // Delete old document if replacing
                if (editingDocument?.document_id && editingDocument.document_id !== documentId) {
                    await actions.deleteDocument(editingDocument.document_id);
                }
            }

            // Validate that we have a document_id (required for new documents)
            if (!documentId && !editingDocument) {
                message.error('Please select a file to upload');
                return;
            }

            const payload = {
                employee: employeeId,
                document_id: documentId,
                document_type: values.type,
                notes: values.description || ''
            };

            let result;
            if (editingDocument) {
                result = await actions.updateEmployeeDocument(editingDocument.id, payload);
            } else {
                result = await actions.createEmployeeDocument(payload);
            }

            if (result !== null) {
                handleCancel();
                fetchDocuments();
            }
        } catch (error) {
            console.error('Error submitting document:', error);
            message.error('Failed to save document');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleView = (record) => {
        if (record.document_url) {
            window.open(record.document_url, '_blank');
        } else {
            message.info('Document URL not available');
        }
    };

    const handleDownload = (record) => {
        if (record.document_url) {
            const link = document.createElement('a');
            link.href = record.document_url;
            link.download = record.fileName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            message.error('Document URL not available');
        }
    };

    const handleDelete = async (record) => {
        try {
            const result = await actions.deleteEmployeeDocument(record.id);
            if (result) {
                // Also delete the underlying document file
                if (record.document_id) {
                    await actions.deleteDocument(record.document_id);
                }
                fetchDocuments();
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            message.error('Failed to delete document');
        }
    };

    const getDocumentStatusColor = (status) => {
        switch (status) {
            case 'verified': return 'green';
            case 'pending': return 'orange';
            case 'rejected': return 'red';
            case 'expired': return 'red';
            case 'renewal-required': return 'yellow';
            default: return 'default';
        }
    };

    const uploadProps = {
        fileList,
        onChange: ({ fileList: newFileList }) => setFileList(newFileList),
        beforeUpload: (file) => {
            const isValidType = file.type === 'application/pdf' || file.type.startsWith('image/');
            if (!isValidType) {
                message.error('You can only upload PDF or image files!');
                return false;
            }
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                message.error('File must be smaller than 10MB!');
                return false;
            }
            return false;
        },
        onRemove: (file) => {
            setFileList(fileList.filter(item => item.uid !== file.uid));
        },
    };

    const documentsColumns = [
        {
            title: 'Document Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{text}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{record.fileName}</div>
                </div>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => <Tag color="blue">{type}</Tag>,
            filters: [
                { text: 'ID Proof', value: 'ID Proof' },
                { text: 'Financial', value: 'Financial' },
                { text: 'Education', value: 'Education' },
                { text: 'Experience', value: 'Experience' },
                { text: 'Medical', value: 'Medical' },
                { text: 'Legal', value: 'Legal' },
                { text: 'Insurance', value: 'Insurance' },
                { text: 'Tax', value: 'Tax' },
                { text: 'Property', value: 'Property' },
                { text: 'Other', value: 'Other' },
            ],
            onFilter: (value, record) => record.type === value,
        },
        {
            title: 'Upload Date',
            dataIndex: 'uploadDate',
            key: 'uploadDate',
            render: (text) => new Date(text).toLocaleDateString(),
            sorter: (a, b) => new Date(a.uploadDate) - new Date(b.uploadDate),
        },
        {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getDocumentStatusColor(status)} style={{ textTransform: 'capitalize' }}>
                    {status}
                </Tag>
            ),
            filters: [
                { text: 'Verified', value: 'verified' },
                { text: 'Pending', value: 'pending' },
                { text: 'Rejected', value: 'rejected' },
                { text: 'Expired', value: 'expired' },
                { text: 'Renewal Required', value: 'renewal-required' },
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Document">
                        <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => handleView(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Edit Document">
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => showModal(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Download Document">
                        <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownload(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Delete Document">
                        <Popconfirm
                            title="Are you sure you want to delete this document?"
                            onConfirm={() => handleDelete(record)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="link"
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // ==================== EVENTS SECTION ====================

    const generateEventsData = () => {
        const events = [];
        const eventTypes = ['holiday', 'birthday', 'company', 'meeting', 'training'];
        const holidays = ['New Year', 'Republic Day', 'Holi', 'Good Friday', 'Independence Day', 'Gandhi Jayanti', 'Diwali', 'Christmas'];
        const firstNames = ['John', 'Jane', 'Mike', 'Sarah', 'David', 'Lisa', 'Robert', 'Emily', 'James', 'Maria', 'Chris', 'Anna', 'Tom', 'Jessica', 'Mark'];
        const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
        const companyEvents = ['Team Building', 'Product Launch', 'Quarterly Review', 'Annual Conference', 'Training Session', 'Workshop', 'Seminar'];

        for (let i = 1; i <= 1000; i++) {
            const type = eventTypes[Math.floor(Math.random() * eventTypes.length)];
            const randomDate = dayjs().subtract(Math.floor(Math.random() * 730), 'days').add(Math.floor(Math.random() * 1460), 'days');

            let title, description;

            switch (type) {
                case 'holiday':
                    title = holidays[Math.floor(Math.random() * holidays.length)];
                    description = 'National Holiday';
                    break;
                case 'birthday':
                    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
                    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
                    title = `${firstName} ${lastName} Birthday`;
                    description = 'Team Member Birthday';
                    break;
                case 'company':
                    title = companyEvents[Math.floor(Math.random() * companyEvents.length)];
                    description = 'Company Event';
                    break;
                case 'meeting':
                    title = `Team Meeting #${i}`;
                    description = 'Department Meeting';
                    break;
                case 'training':
                    title = `Training Session ${i}`;
                    description = 'Skills Development';
                    break;
                default:
                    title = `Event ${i}`;
                    description = 'General Event';
            }

            events.push({
                id: i,
                date: randomDate.format('YYYY-MM-DD'),
                title,
                type,
                description
            });
        }

        return events.sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix());
    };

    const eventsData = generateEventsData();

    const getEventTypeColor = (type) => {
        switch (type) {
            case 'holiday': return 'red';
            case 'birthday': return 'blue';
            case 'company': return 'green';
            case 'meeting': return 'orange';
            case 'training': return 'purple';
            default: return 'default';
        }
    };

    const getEventIcon = (type) => {
        switch (type) {
            case 'holiday': return <StarOutlined />;
            case 'birthday': return <GiftOutlined />;
            case 'company': return <CalendarOutlined />;
            default: return <CalendarOutlined />;
        }
    };

    const getEventsForDate = (date) => {
        const dateStr = date.format('YYYY-MM-DD');
        return eventsData.filter(event => event.date === dateStr);
    };

    const dateCellRender = (value) => {
        const events = getEventsForDate(value);
        if (events.length === 0) return null;

        return (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {events.map(event => (
                    <li key={event.id}>
                        <Badge
                            color={getEventTypeColor(event.type)}
                            text={event.title.length > 15 ? `${event.title.substring(0, 15)}...` : event.title}
                            style={{ fontSize: '10px' }}
                        />
                    </li>
                ))}
            </ul>
        );
    };

    const upcomingEvents = eventsData
        .filter(event => dayjs(event.date).isAfter(dayjs(), 'day'))
        .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
        .slice(0, 50);

    // ==================== RENDER SECTIONS ====================

    const renderDocuments = () => {
        return (
            <>
                {false && (
                    <Alert
                        message="Profile Locked"
                        description="Document upload, download, and deletion functionality is disabled while your profile is locked."
                        type="warning"
                        showIcon
                        style={{ marginBottom: '20px' }}
                    />
                )}
                <Card
                    title="My Documents"
                    extra={
                        <Space>
                            <Popover
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                        <span style={{ fontSize: 14, fontWeight: 600 }}>Required Documents Checklist</span>
                                    </div>
                                }
                                content={
                                    <div style={{ width: 360, maxHeight: 320, overflowY: 'auto' }}>
                                        {[
                                            { label: 'ID Proof', desc: 'Aadhar Card', tag: 'Required', color: '#f5222d' },
                                            { label: 'Permanent Address Proof', desc: 'Aadhar Card / Rental Agreement / Electricity Bill / Gas Bill', tag: 'Required', color: '#f5222d' },
                                            { label: 'Tax Proof', desc: 'PAN Card', tag: 'Required', color: '#f5222d' },
                                            { label: 'Financial Document', desc: 'Bank Passbook / Bank Statement', tag: 'Required', color: '#f5222d' },
                                            { label: 'Education Proof', desc: 'Degree Certificates, Marksheets', tag: 'Required', color: '#f5222d' },
                                            { label: 'Experience', desc: 'Offer Letters, Relieving Letters', tag: 'Required', color: '#f5222d' },
                                            { label: 'Medical', desc: 'Fitness Certificate from any valid physician', tag: 'Required', color: '#f5222d' },
                                            { label: 'Passport Size Photo', desc: 'Recent photograph', tag: 'Required', color: '#f5222d' },
                                            { label: 'Current Address Proof', desc: 'If different from permanent address', tag: 'Optional', color: '#8c8c8c' },
                                        ].map((item, i) => (
                                            <div key={i} style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                                                padding: '8px 0', borderBottom: i < 8 ? '1px solid #f0f0f0' : 'none'
                                            }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13, color: '#262626' }}>{item.label}</div>
                                                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>{item.desc}</div>
                                                </div>
                                                <Tag color={item.color} style={{ marginLeft: 8, flexShrink: 0, fontSize: 11 }}>{item.tag}</Tag>
                                            </div>
                                        ))}
                                    </div>
                                }
                                placement="bottomRight"
                                trigger="hover"
                                overlayStyle={{ maxWidth: 420 }}
                            >
                                <Button type="default" icon={<InfoCircleOutlined />}>Info</Button>
                            </Popover>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={showModal}
                            >
                                Upload Document
                            </Button>
                        </Space>
                    }
                >
                    <Table
                        columns={documentsColumns}
                        dataSource={documents}
                        loading={documentsLoading}
                        rowKey="id"
                        pagination={{
                            defaultPageSize: 20,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        scroll={{ y: 400 }}
                    />
                </Card>

                <Modal
                    title={editingDocument ? "Update Document" : "Upload Document"}
                    open={isModalVisible}
                    onCancel={handleCancel}
                    footer={null}
                    width={600}
                >
                    <Form form={form} layout="vertical" onFinish={handleSubmit}>
                        <Form.Item
                            label="Document Name"
                            name="name"
                            rules={[{ required: true, message: 'Please enter document name!' }]}
                        >
                            <Input placeholder="Enter document name" />
                        </Form.Item>

                        <Form.Item
                            label="Document Type"
                            name="type"
                            rules={[{ required: true, message: 'Please select document type!' }]}
                        >
                            <Select placeholder="Select document type">
                                <Option value="ID Proof">ID Proof</Option>
                                <Option value="Financial">Financial</Option>
                                <Option value="Education">Education</Option>
                                <Option value="Experience">Experience</Option>
                                <Option value="Medical">Medical</Option>
                                <Option value="Legal">Legal</Option>
                                <Option value="Insurance">Insurance</Option>
                                <Option value="Tax">Tax</Option>
                                <Option value="Property">Property</Option>
                                <Option value="Other">Other</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item label="Description (Optional)" name="description">
                            <Input.TextArea
                                rows={3}
                                placeholder="Add any additional description about the document..."
                            />
                        </Form.Item>

                        <Form.Item label={editingDocument ? "Replace File (Optional)" : "Upload File"}>
                            {editingDocument && (
                                <Alert
                                    message={`Current file: ${editingDocument.fileName}`}
                                    type="info"
                                    style={{ marginBottom: 8 }}
                                />
                            )}
                            <Dragger {...uploadProps}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                                <p className="ant-upload-hint">
                                    Support for PDF and image files. Maximum file size is 10MB.
                                </p>
                            </Dragger>
                        </Form.Item>

                        <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                            <Space>
                                <Button onClick={handleCancel}>Cancel</Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitLoading}
                                    disabled={!editingDocument && fileList.length === 0}
                                >
                                    {editingDocument ? "Update Document" : "Upload Document"}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </>
        );
    };

    const renderEvents = () => {
        return (
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={16}>
                    <Card title="Events Calendar" style={{ height: '600px' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <Select
                                defaultValue="all"
                                style={{ width: 200 }}
                                onChange={setViewMode}
                                placeholder="Filter events"
                            >
                                <Option value="all">All Events</Option>
                                <Option value="holiday">Holidays</Option>
                                <Option value="birthday">Birthdays</Option>
                                <Option value="company">Company Events</Option>
                                <Option value="meeting">Meetings</Option>
                                <Option value="training">Training</Option>
                            </Select>
                        </div>
                        <Calendar
                            dateCellRender={dateCellRender}
                            onSelect={setSelectedDate}
                            value={selectedDate}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card title="Upcoming Events" style={{ marginBottom: '16px', height: '400px' }}>
                        <List
                            dataSource={upcomingEvents}
                            style={{ height: '320px', overflow: 'auto' }}
                            renderItem={event => (
                                <List.Item>
                                    <List.Item.Meta
                                        avatar={getEventIcon(event.type)}
                                        title={
                                            <div>
                                                {event.title}
                                                <Tag color={getEventTypeColor(event.type)} style={{ marginLeft: '8px' }}>
                                                    {event.type}
                                                </Tag>
                                            </div>
                                        }
                                        description={
                                            <div>
                                                <div>{dayjs(event.date).format('MMM DD, YYYY')}</div>
                                                <div style={{ color: '#666' }}>{event.description}</div>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>

                    <Card title="Event Legend">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Badge color="red" />
                                <StarOutlined />
                                <span>Holidays</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Badge color="blue" />
                                <GiftOutlined />
                                <span>Birthdays</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Badge color="green" />
                                <CalendarOutlined />
                                <span>Company Events</span>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>
        );
    };

    const renderAttendance = () => {
        return <EmployeeAttendance employeeId={employeeId} showAddButton={false} urlPrefix="hr_att_" />;
    };

    const getSectionTitle = () => {
        const titles = {
            'documents': 'Documents',
            'events': 'Events',
            'attendance': 'Attendance'
        };
        return titles[activeSection] || 'HR Information';
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'documents':
                return renderDocuments();
            case 'events':
                return renderEvents();
            case 'attendance':
                return renderAttendance();
            default:
                return <Text>Section not found</Text>;
        }
    };

    return (
        <div style={{ height: 'calc(100vh - 280px)', padding: '20px' }}>
            <Card
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                bodyStyle={{ flex: 1, padding: 0, overflow: 'hidden' }}
            >
                <Layout style={{ flex: 1, background: '#fff', overflow: 'hidden' }}>
                    {/* Sidebar */}
                    {!hideSider && (
                        <Sider
                            width={240}
                            style={{
                                background: '#fff',
                                borderRight: '1px solid #f0f0f0',
                                overflow: 'auto'
                            }}
                        >
                            <style>
                                {`
                                    .hr-sidebar-menu .ant-menu-item-selected {
                                        background-color: #1890ff !important;
                                        color: #fff !important;
                                    }
                                    .hr-sidebar-menu .ant-menu-item-selected .anticon {
                                        color: #fff !important;
                                    }
                                    .hr-sidebar-menu .ant-menu-item-selected::after {
                                        border-right-color: #1890ff !important;
                                    }
                                `}
                            </style>
                            <Menu
                                mode="inline"
                                selectedKeys={[activeSection]}
                                onClick={({ key }) => setActiveSection(key)}
                                style={{ borderRight: 0 }}
                                items={menuItems}
                                className="hr-sidebar-menu"
                            />
                        </Sider>
                    )}

                    {/* Content Area */}
                    <Content style={{ padding: '24px', overflow: 'auto', background: '#fff' }}>
                        <Card
                            title="Employee Verification"
                            size="small"
                            style={{ marginBottom: 16 }}
                            extra={
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    size="small"
                                    loading={verificationLoading}
                                    onClick={handleSaveVerification}
                                >
                                    Save
                                </Button>
                            }
                        >
                            <Row gutter={16} align="middle">
                                <Col>
                                    <Space>
                                        <Text strong>Verified:</Text>
                                        <Switch
                                            checked={isVerified}
                                            onChange={(checked) => {
                                                setIsVerified(checked);
                                                if (checked) setUnverifiedReason('');
                                            }}
                                            checkedChildren="Yes"
                                            unCheckedChildren="No"
                                        />
                                    </Space>
                                </Col>
                                {!isVerified && (
                                    <Col flex="auto">
                                        <Space style={{ width: '100%' }}>
                                            <Text strong>Reason:</Text>
                                            <Input
                                                value={unverifiedReason}
                                                onChange={(e) => setUnverifiedReason(e.target.value)}
                                                placeholder="Enter reason for unverified status"
                                                style={{ width: 400 }}
                                            />
                                        </Space>
                                    </Col>
                                )}
                            </Row>
                        </Card>
                        <Title level={5} style={{ marginBottom: 24 }}>
                            {getSectionTitle()}
                        </Title>
                        {renderContent()}
                    </Content>
                </Layout>
            </Card>
        </div>
    );
};

export default EmployeeHR;
